import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  StarIcon, 
  UserCircleIcon,
  ChevronDownIcon,
  CheckBadgeIcon
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import { formatDistanceToNow } from 'date-fns';
import { useAppSelector, useAppDispatch } from '../../hooks/redux';
import { fetchUserReviews } from '../../store/slices/reviewsSlice';
import Button from '../common/Button';
import Avatar from '../common/Avatar';

interface ReviewsSectionProps {
  userId: string;
  highlightReviewId?: string;
}

const ReviewsSection: React.FC<ReviewsSectionProps> = ({ userId, highlightReviewId }) => {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<'all' | '5' | '4' | '3' | '2' | '1'>('all');
  const [sortBy, setSortBy] = useState<'newest' | 'oldest' | 'highest' | 'lowest'>('newest');
  
  const dispatch = useAppDispatch();
  const { reviews, loading, stats, page, hasMore } = useAppSelector((state) => state.reviews);
  const { user: currentUser } = useAppSelector((state) => state.auth);

  // Refs for scrolling
  const reviewRefs = useRef<{[key: string]: HTMLDivElement | null}>({});

  useEffect(() => {
    dispatch(fetchUserReviews({ userId, page: 1 }));
  }, [dispatch, userId]);

  // Scroll to highlighted review when reviews are loaded
  useEffect(() => {
    if (highlightReviewId && reviews.length > 0) {
      let attempts = 0;
      const maxAttempts = 5;
      
      const tryScroll = () => {
        const element = reviewRefs.current[highlightReviewId];
        
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } else if (attempts < maxAttempts) {
          attempts++;
          setTimeout(tryScroll, 500); // Retry every 500ms
        }
      };

      tryScroll();
    }
  }, [highlightReviewId, reviews]);

  const renderStars = (rating: number, size: 'sm' | 'md' | 'lg' = 'md') => {
    const sizeClass = size === 'sm' ? 'h-3 w-3' : size === 'md' ? 'h-4 w-4' : 'h-5 w-5';
    
    return Array.from({ length: 5 }, (_, i) => (
      <StarIconSolid
        key={i}
        className={`${sizeClass} ${
          i < Math.floor(rating) ? 'text-yellow-400' : 'text-gray-200 dark:text-gray-700'
        }`}
      />
    ));
  };

  const filteredAndSortedReviews = reviews
    .filter(review => filter === 'all' || review.rating === parseInt(filter))
    .sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
        case 'oldest':
          return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
        case 'highest':
          return b.rating - a.rating;
        case 'lowest':
          return a.rating - b.rating;
        default:
          return 0;
      }
    });

  const ratingDistribution = [5, 4, 3, 2, 1].map(rating => {
    const count = reviews.filter(review => review.rating === rating).length;
    const percentage = reviews.length > 0 ? (count / reviews.length) * 100 : 0;
    return { rating, count, percentage };
  });

  if (loading) {
    return (
      <div className="space-y-8 mb-10 animate-pulse">
        {/* Summary Card Skeleton */}
        <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 md:p-8 border border-gray-100 dark:border-gray-800 shadow-sm h-48">
          <div className="flex flex-col md:flex-row gap-8 items-center h-full">
            <div className="md:w-1/3 h-full bg-gray-100 dark:bg-gray-800 rounded-lg"></div>
             <div className="w-full md:w-2/3 space-y-3">
              {[1, 2, 3, 4, 5].map(i => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-12 h-4 bg-gray-100 dark:bg-gray-800 rounded"></div>
                  <div className="flex-1 h-2 bg-gray-100 dark:bg-gray-800 rounded-full"></div>
                  <div className="w-8 h-4 bg-gray-100 dark:bg-gray-800 rounded"></div>
                </div>
              ))}
            </div>
          </div>
        </div>

         {/* Filters Skeleton */}
        <div className="flex justify-between items-center h-10">
          <div className="w-32 h-6 bg-gray-100 dark:bg-gray-800 rounded"></div>
          <div className="flex gap-3">
            <div className="w-32 h-10 bg-gray-100 dark:bg-gray-800 rounded-lg"></div>
            <div className="w-32 h-10 bg-gray-100 dark:bg-gray-800 rounded-lg"></div>
          </div>
        </div>

        {/* Reviews Grid Skeleton */}
       <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 2xl:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-white dark:bg-gray-900 rounded-xl p-6 border border-gray-100 dark:border-gray-800 h-48 flex flex-col gap-4">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-gray-100 dark:bg-gray-800 rounded-full shrink-0"></div>
                <div className="flex-1 space-y-2">
                  <div className="w-32 h-4 bg-gray-100 dark:bg-gray-800 rounded"></div>
                  <div className="w-20 h-3 bg-gray-100 dark:bg-gray-800 rounded"></div>
                </div>
              </div>
              <div className="w-24 h-4 bg-gray-100 dark:bg-gray-800 rounded"></div>
              <div className="flex-1 space-y-2">
                <div className="w-full h-3 bg-gray-100 dark:bg-gray-800 rounded"></div>
                <div className="w-full h-3 bg-gray-100 dark:bg-gray-800 rounded"></div>
                <div className="w-2/3 h-3 bg-gray-100 dark:bg-gray-800 rounded"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8 mb-10">
      {/* Reviews Summary Card - Redesigned */}
      {reviews.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-8"
        >
          <div className="flex flex-col md:flex-row gap-8 items-center">
            {/* Overall Rating */}
            <div className="text-center md:text-left md:w-1/3 md:border-r md:border-gray-100 dark:md:border-gray-800 md:pr-8">
              <div className="flex items-baseline justify-center md:justify-start space-x-2 mb-2">
                <span className="text-5xl font-bold text-gray-900 dark:text-white tracking-tight">
                  {stats?.averageRating?.toFixed(1) || '0.0'}
                </span>
                <span className="text-gray-400 dark:text-gray-500 text-lg font-medium">/ 5</span>
              </div>
              <div className="flex items-center justify-center md:justify-start mb-3 space-x-1">
                {renderStars(stats?.averageRating || 0, 'lg')}
              </div>
              <p className="text-gray-500 dark:text-gray-400 font-medium text-sm">
                Based on {reviews.length} review{reviews.length !== 1 ? 's' : ''}
              </p>
            </div>

            {/* Rating Distribution */}
            <div className="w-full md:w-2/3 space-y-3">
              {ratingDistribution.map(({ rating, count, percentage }) => (
                <div key={rating} className="flex items-center group">
                  <div className="flex items-center space-x-2 w-16 flex-shrink-0">
                    <span className="text-sm font-bold text-gray-700 dark:text-gray-300">{rating} Stars</span>
                  </div>
                  <div className="flex-1 h-2.5 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden mx-3">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${percentage}%` }}
                      transition={{ duration: 1, ease: "easeOut" }}
                      className="h-full bg-black dark:bg-white rounded-full"
                    />
                  </div>
                  <span className="text-sm text-gray-400 dark:text-gray-500 w-8 text-right tabular-nums font-medium">{count}</span>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      )}

      {/* Filters and Sorting */}
      {reviews.length > 0 && (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 py-2">
          <h3 className="text-lg font-bold text-gray-900 dark:text-white">
            All Reviews <span className="text-gray-400 dark:text-gray-500 font-normal ml-1 text-base">({reviews.length})</span>
          </h3>
          
          <div className="flex items-center space-x-3">
            <div className="relative group">
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value as any)}
                className="appearance-none bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 py-2.5 pl-4 pr-10 rounded-xl text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-black/5 dark:focus:ring-white/5 focus:border-black dark:focus:border-white cursor-pointer hover:border-black dark:hover:border-white transition-all shadow-sm"
              >
                <option value="all">All Ratings</option>
                <option value="5">5 Stars</option>
                <option value="4">4 Stars</option>
                <option value="3">3 Stars</option>
                <option value="2">2 Stars</option>
                <option value="1">1 Star</option>
              </select>
              <ChevronDownIcon className="w-4 h-4 text-gray-400 group-hover:text-black absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none transition-colors" />
            </div>

            <div className="relative group">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as any)}
                className="appearance-none bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 py-2.5 pl-4 pr-10 rounded-xl text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-black/5 dark:focus:ring-white/5 focus:border-black dark:focus:border-white cursor-pointer hover:border-black dark:hover:border-white transition-all shadow-sm"
              >
                <option value="newest">Newest</option>
                <option value="oldest">Oldest</option>
                <option value="highest">Highest Rated</option>
                <option value="lowest">Lowest Rated</option>
              </select>
              <ChevronDownIcon className="w-4 h-4 text-gray-400 group-hover:text-black absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none transition-colors" />
            </div>
          </div>
        </div>
      )}

      {/* Reviews List - Grid on Desktop */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 2xl:grid-cols-4 gap-6">
        {filteredAndSortedReviews.length > 0 ? (
          filteredAndSortedReviews.map((review, index) => {
            const isHighlighted = review.id === highlightReviewId;
            
            return (
              <motion.div
                key={review.id}
                ref={(el) => { 
                  if (el) {
                    reviewRefs.current[review.id] = el; 
                  }
                }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ 
                  opacity: 1, 
                  y: 0,
                  scale: isHighlighted ? [1, 1.02, 1] : 1,
                  borderColor: isHighlighted ? ['#e5e7eb', '#000000', '#e5e7eb'] : '#f3f4f6',
                  boxShadow: isHighlighted ? "0 0 0 2px rgba(0, 0, 0, 0.1)" : "0 1px 2px 0 rgba(0, 0, 0, 0.05)"
                }}
                transition={{ 
                  duration: 0.4, 
                  delay: index * 0.05,
                  scale: { duration: 0.5, times: [0, 0.5, 1] },
                  borderColor: { duration: 2, repeat: isHighlighted ? 1 : 0 }
                }}
                className={`bg-white dark:bg-gray-900 rounded-2xl p-5 border ${
                  isHighlighted ? 'border-black dark:border-white ring-4 ring-gray-100 dark:ring-gray-800' : 'border-gray-100 dark:border-gray-800'
                } hover:border-black dark:hover:border-white hover:shadow-xl transition-all duration-300 flex flex-col h-full group relative`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    {/* Reviewer Avatar */}
                    <div 
                        className="flex-shrink-0 cursor-pointer hover:scale-105 transition-transform"
                        onClick={(e) => {
                            e.stopPropagation();
                            if (review.reviewer?.id) {
                                navigate(`/profile/${review.reviewer.id}`);
                                window.scrollTo(0, 0);
                            }
                        }}
                    >
                      <Avatar 
                        src={review.reviewer?.profile_pic_signed || review.reviewer?.profile_pic} 
                        alt={`${review.reviewer?.first_name || 'DELETED USER'}`}
                        size="md"
                        className="w-10 h-10 border-2 border-white dark:border-gray-800 shadow-sm ring-1 ring-gray-100 dark:ring-gray-700"
                      />
                    </div>

                    <div>
                      <h4 
                          className="font-bold text-gray-900 dark:text-white cursor-pointer hover:text-black dark:hover:text-white transition-colors text-sm"
                          onClick={(e) => {
                              e.stopPropagation();
                              if (review.reviewer?.id) {
                              navigate(`/profile/${review.reviewer.id}`);
                              window.scrollTo(0, 0);
                          }
                          }}
                      >
                        {review.reviewer?.first_name} {review.reviewer?.last_name}
                      </h4>
                      <p className="text-[10px] text-gray-500 dark:text-gray-400 capitalize font-medium tracking-wide">
                        {review.reviewer?.user_type}
                      </p>
                    </div>
                  </div>

                  <span className="text-[10px] text-gray-400 dark:text-gray-500 font-medium bg-gray-50 dark:bg-gray-800 px-2 py-1 rounded-full border border-gray-100 dark:border-gray-700">
                    {formatDistanceToNow(new Date(review.created_at), { addSuffix: true })}
                  </span>
                </div>

                {/* Rating & Verification */}
                <div className="flex items-center justify-between mb-4 bg-gray-50 dark:bg-gray-800 p-2 rounded-lg border border-gray-100 dark:border-gray-700">
                  <div className="flex items-center space-x-1">
                    {renderStars(review.rating, 'sm')}
                  </div>
                  {review.booking && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold bg-white dark:bg-gray-900 text-green-700 dark:text-green-400 border border-green-100 dark:border-green-900 shadow-sm uppercase tracking-wider">
                      <CheckBadgeIcon className="w-3 h-3 mr-1" />
                      Verified
                    </span>
                  )}
                </div>

                {/* Content */}
                {review.comment && (
                  <div className="flex-grow mb-4">
                    <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed line-clamp-4 hover:line-clamp-none transition-all duration-200">
                      "{review.comment}"
                    </p>
                  </div>
                )}

                {/* Project Context */}
                {review.booking && (
                  <div 
                      className="mt-auto pt-3 border-t border-gray-100 dark:border-gray-800 cursor-pointer group/project flex items-center justify-between"
                      onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/profile/${userId}/projects/${review.booking_id}`);
                      }}
                  >
                    <div className="flex flex-col">
                      <span className="text-[10px] text-gray-400 dark:text-gray-500 font-bold uppercase tracking-wider mb-0.5">Project</span>
                      <span className="text-xs font-bold text-gray-900 group-hover/project:text-black dark:group-hover/project:text-white line-clamp-1">
                        {review.booking.title || 'Project'}
                      </span>
                    </div>
                    <div className="w-6 h-6 rounded-full bg-gray-50 dark:bg-gray-800 flex items-center justify-center group-hover/project:bg-black dark:group-hover/project:bg-white group-hover/project:text-white dark:group-hover/project:text-black transition-colors">
                       <ChevronDownIcon className="w-3 h-3 -rotate-90" />
                    </div>
                  </div>
                )}
              </motion.div>
            );
          })
        ) : (
          <div className="col-span-1 md:col-span-2">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center py-12 bg-gray-50 dark:bg-gray-800/50 rounded-2xl border border-dashed border-gray-200 dark:border-gray-700"
            >
              <div className="w-12 h-12 bg-white dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4 shadow-sm">
                <StarIcon className="h-6 w-6 text-gray-300 dark:text-gray-600" />
              </div>
              <h3 className="text-base font-bold text-gray-900 dark:text-white mb-2">
                {filter === 'all' ? 'No reviews yet' : `No ${filter}-star reviews`}
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 max-w-sm mx-auto px-4">
                {userId === currentUser?.id 
                  ? (
                    <div className="space-y-4 mt-2">
                      <p>Complete your first project to start receiving reviews from collaborators.</p>
                      <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-xl border border-blue-100 dark:border-blue-800/30 text-left max-w-xs mx-auto">
                        <h4 className="font-bold text-blue-900 dark:text-blue-100 mb-2 text-sm flex items-center">
                          <span className="mr-2">💡</span> How to get reviews?
                        </h4>
                        <p className="text-xs text-blue-700 dark:text-blue-300 leading-relaxed">
                          There is only one way to get reviews:
                          <br/><br/>
                          <strong>Invite collaborators</strong> to your collaborations.  those users will give you ratings and reviews.
                        </p>
                      </div>
                    </div>
                  )
                  : 'This user hasn\'t received any reviews yet.'
                }
              </p>
            </motion.div>
          </div>
        )}
      </div>

      {/* Infinite Scroll Trigger */}
      {hasMore && !loading && (
        <div 
            className="w-full flex justify-center py-8"
            ref={(node) => {
                if (node && hasMore && !loading) {
                    const observer = new IntersectionObserver((entries) => {
                        if (entries[0].isIntersecting) {
                            dispatch(fetchUserReviews({ userId, page: page + 1 }));
                        }
                    }, { rootMargin: '400px' });
                    observer.observe(node);
                    return () => observer.disconnect();
                }
            }}
        >
            <div className="animate-spin rounded-full h-6 w-6 border-2 border-gray-200 dark:border-gray-800 border-t-black dark:border-t-white"></div>
        </div>
      )}
    </div>
  );
};

export default ReviewsSection;
